import {useState} from 'react';
import {TFunction} from 'i18next';

interface ValidationErrors {
  alertName?: string;
  machine?: string;
  from?: string;
  to?: string;
  fixTime?: string;
  notifications?: string;
  timeRange?: string;
  newEmail?: string;
}

/**
 * 時刻文字列を分に変換するヘルパー関数
 * エラーハンドリング付き
 */
const parseTimeToMinutes = (time: string): number | null => {
  if (!time || typeof time !== 'string') return null;

  const parts = time.split(':');
  if (parts.length !== 2) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return hours * 60 + minutes;
};

interface UseAlertValidationProps {
  t: TFunction;
  enqueueSnackbar: any;
}

/**
 * アラート設定のバリデーションロジックを管理するフック
 */
export const useAlertValidation = ({t, enqueueSnackbar}: UseAlertValidationProps) => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  /**
   * 個別フィールドのバリデーション
   */
  const validateField = (field: string, value: any, contextValues?: any): string | undefined => {
    const ctx = contextValues || {};

    switch (field) {
      case 'alertName':
        if (!value || value.trim() === '') {
          return t('アラート名を入力してください');
        }
        if (value.length > 100) {
          return t('アラート名は100文字以内で入力してください');
        }
        break;

      case 'machine':
        if (!value || value === '') {
          return t('監視装置を選択してください');
        }
        break;

      case 'from':
        if (ctx.scheduleType === '1' && (!value || value === '')) {
          return t('期間Fromを入力してください');
        }
        if (ctx.scheduleType === '1' && ctx.to && value >= ctx.to) {
          return t('期間Fromは期間Toより前に設定してください');
        }
        break;

      case 'to':
        if (ctx.scheduleType === '1' && (!value || value === '')) {
          return t('期間Toを入力してください');
        }
        if (ctx.scheduleType === '1' && ctx.from && value <= ctx.from) {
          return t('期間Toは期間Fromより後に設定してください');
        }
        break;

      case 'fixTime':
        if (ctx.scheduleType === '2' && (!value || value === '')) {
          return t('時刻を入力してください');
        }
        break;

      case 'timeRange':
        if (ctx.scheduleType === '1' && ctx.from && ctx.to && ctx.interval) {
          const fromMinutes = parseTimeToMinutes(ctx.from);
          const toMinutes = parseTimeToMinutes(ctx.to);

          if (fromMinutes === null || toMinutes === null) {
            return t('時刻の形式が正しくありません');
          }

          const intervalMinutes = parseInt(ctx.interval, 10) * 60;
          const duration = toMinutes - fromMinutes;

          if (duration < intervalMinutes) {
            return t(
              '判定頻度が期間より長く設定されています。期間を延ばすか、判定頻度を短くしてください。'
            );
          }
        }
        break;
    }

    return undefined;
  };

  /**
   * 全体バリデーション（保存時）
   */
  const validateAll = (formValues: {
    alertName: string;
    machine: string;
    days: string[];
    scheduleType: string;
    from: string;
    to: string;
    interval: string;
    fixTime: string;
  }): boolean => {
    const errors: ValidationErrors = {};

    errors.alertName = validateField('alertName', formValues.alertName);
    errors.machine = validateField('machine', formValues.machine);

    // 曜日チェック
    if (formValues.days.length === 0) {
      enqueueSnackbar(t('曜日を少なくとも1つ選択してください'), {
        variant: 'error',
        preventDuplicate: true
      });
      return false;
    }

    if (formValues.scheduleType === '1') {
      const ctx = {
        from: formValues.from,
        to: formValues.to,
        scheduleType: formValues.scheduleType,
        interval: formValues.interval
      };
      errors.from = validateField('from', formValues.from, ctx);
      errors.to = validateField('to', formValues.to, ctx);
      errors.timeRange = validateField('timeRange', null, ctx);
    } else {
      errors.fixTime = validateField('fixTime', formValues.fixTime, {
        scheduleType: formValues.scheduleType
      });
    }

    setValidationErrors(errors);

    // エラーがあるかチェック
    const hasErrors = Object.values(errors).some(error => error !== undefined);
    if (hasErrors) {
      enqueueSnackbar(t('入力内容に誤りがあります。赤枠のフィールドを確認してください。'), {
        variant: 'error',
        preventDuplicate: true
      });
    }
    return !hasErrors;
  };

  /**
   * バリデーションエラーをクリア
   */
  const clearValidationErrors = () => {
    setValidationErrors({});
  };

  /**
   * 特定フィールドのバリデーションエラーを更新
   */
  const updateValidationError = (field: keyof ValidationErrors, error: string | undefined) => {
    setValidationErrors(prev => ({...prev, [field]: error}));
  };

  /**
   * 複数フィールドのバリデーションエラーを更新
   */
  const updateValidationErrors = (errors: Partial<ValidationErrors>) => {
    setValidationErrors(prev => ({...prev, ...errors}));
  };

  /**
   * フィールド変更時のバリデーション付きハンドラーファクトリ
   */
  const createValidationHandler = (
    field: string,
    setter: (value: any) => void,
    getContextValues?: () => any
  ) => {
    return (value: any) => {
      setter(value);
      const ctx = getContextValues ? getContextValues() : undefined;
      const error = validateField(field, value, ctx);
      updateValidationError(field as keyof ValidationErrors, error);
    };
  };

  return {
    validationErrors,
    validateField,
    validateAll,
    clearValidationErrors,
    updateValidationError,
    updateValidationErrors,
    createValidationHandler
  };
};
